import { Controller } from "@hotwired/stimulus"

// Makes a dialog draggable by a handle inside it.
// Usage:
//   <div data-controller="draggable">
//     <div data-draggable-target="handle">Header</div>
//     ...
//   </div>
export default class extends Controller {
  static targets = ["handle"]

  connect() {
    this._onMouseMove = this._onMouseMove.bind(this)
    this._onMouseUp = this._onMouseUp.bind(this)
    this._onTouchMove = this._onTouchMove.bind(this)
    this._onTouchEnd = this._onTouchEnd.bind(this)

    if (this.hasHandleTarget) {
      this.handleTarget.addEventListener('mousedown', this._onMouseDown)
      this.handleTarget.addEventListener('touchstart', this._onTouchStart, { passive: false })
    }
  }

  disconnect() {
    this._teardown()
  }

  // Lazily bound to preserve `this`
  get _onMouseDown() {
    if (!this.__onMouseDown) this.__onMouseDown = this._startDrag.bind(this)
    return this.__onMouseDown
  }

  get _onTouchStart() {
    if (!this.__onTouchStart) this.__onTouchStart = (e) => {
      e.preventDefault()
      const t = e.touches[0]
      this._startDrag(t)
    }
    return this.__onTouchStart
  }

  _startDrag(e) {
    // Compute initial offset from cursor to element top-left
    const rect = this.element.getBoundingClientRect()
    this.offsetX = e.clientX - rect.left
    this.offsetY = e.clientY - rect.top

    // Switch to fixed positioning so dragging is relative to viewport
    this.element.style.position = 'fixed'
    this.element.style.left = `${rect.left}px`
    this.element.style.top = `${rect.top}px`
    this.element.style.margin = '0'
    this.element.style.right = 'auto'
    this.element.style.bottom = 'auto'

    this.dragging = true

    window.addEventListener('mousemove', this._onMouseMove)
    window.addEventListener('mouseup', this._onMouseUp)
    window.addEventListener('touchmove', this._onTouchMove, { passive: false })
    window.addEventListener('touchend', this._onTouchEnd)
  }

  _onMouseMove(e) {
    if (!this.dragging) return
    this._moveTo(e.clientX, e.clientY)
  }

  _onTouchMove(e) {
    if (!this.dragging) return
    e.preventDefault()
    const t = e.touches[0]
    this._moveTo(t.clientX, t.clientY)
  }

  _moveTo(clientX, clientY) {
    const left = clientX - this.offsetX
    const top = clientY - this.offsetY

    // Clamp within viewport with 8px padding
    const maxLeft = Math.max(0, window.innerWidth - this.element.offsetWidth - 8)
    const maxTop = Math.max(0, window.innerHeight - this.element.offsetHeight - 8)
    const clampedLeft = Math.min(Math.max(8, left), maxLeft)
    const clampedTop = Math.min(Math.max(8, top), maxTop)

    this.element.style.left = `${clampedLeft}px`
    this.element.style.top = `${clampedTop}px`
  }

  _onMouseUp() { this._teardown() }
  _onTouchEnd() { this._teardown() }

  _teardown() {
    if (!this.dragging) return
    this.dragging = false
    window.removeEventListener('mousemove', this._onMouseMove)
    window.removeEventListener('mouseup', this._onMouseUp)
    window.removeEventListener('touchmove', this._onTouchMove)
    window.removeEventListener('touchend', this._onTouchEnd)
  }
}

